import { SubscriptionCount, SubscriptionDetails } from "../../types"
import {
  getMonth,
  groupByYearMonth,
  parseYearMonthKey,
  sortYearMonthKeys
} from "."

export const getSubscriberDisplayName = (
  subscription:
    | Pick<SubscriptionDetails, "first_name" | "last_name">
    | undefined,
  subscriptionId: string
): string => {
  const first = subscription?.first_name ?? ""
  const last = subscription?.last_name ?? ""
  return `${first} ${last}`.trim() || `Subscription #${subscriptionId}`
}

export const getSubscriberInitials = (
  subscription:
    | Pick<SubscriptionDetails, "first_name" | "last_name">
    | undefined
): string => {
  const first = subscription?.first_name ?? ""
  const last = subscription?.last_name ?? ""
  if (!first && !last) return "?"
  return `${first[0] ?? ""}${last[0] ?? ""}`
}

export interface MonthlySubscriptionCount {
  month: string
  year: string
  activeCount: number
  disabledCount: number
  deletedCount: number
  totalCount: number
}

// Helper to aggregate per-year-month
const aggregate = (data: SubscriptionCount[]): Record<string, number> =>
  groupByYearMonth(
    data,
    item => item.period_start,
    item => item.subscription_count
  )

/**
 * Groups daily subscription count data (active/disabled/deleted) into monthly totals by year.
 * Includes both current and previous year if in range.
 */
export const groupSubscriptionCountByMonth = (
  activeSubscriptionCounts: SubscriptionCount[],
  disabledSubscriptionCounts: SubscriptionCount[],
  deletedSubscriptionCounts: SubscriptionCount[]
): MonthlySubscriptionCount[] => {
  const activeTotals = aggregate(activeSubscriptionCounts)
  const disabledTotals = aggregate(disabledSubscriptionCounts)
  const deletedTotals = aggregate(deletedSubscriptionCounts)

  // Get all unique year-month keys across all three datasets
  const uniqueKeys = new Set([
    ...Object.keys(activeTotals),
    ...Object.keys(disabledTotals),
    ...Object.keys(deletedTotals)
  ])

  // Convert to array and sort by year then month
  const sortedKeys = sortYearMonthKeys(Array.from(uniqueKeys))

  // Build final array only for months that have data
  const result: MonthlySubscriptionCount[] = sortedKeys.map(key => {
    const { year, month } = parseYearMonthKey(key)
    const monthName = getMonth(year, month)
    const activeCount = activeTotals[key] || 0
    const disabledCount = disabledTotals[key] || 0
    const deletedCount = deletedTotals[key] || 0

    return {
      month: monthName,
      year: year.toString(),
      activeCount,
      disabledCount,
      deletedCount,
      totalCount: activeCount + disabledCount + deletedCount
    }
  })

  return result
}
